import { TZDateMini } from '@date-fns/tz';
import { isAfter } from 'date-fns';
import { useCallback, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { parseTimeAsTimeValue } from '@dynatrace-sdk/units';
import { getTimezone } from '@dynatrace-sdk/user-preferences';

import { Button } from '../../../buttons/button/Button.js';
import { isNowTimeValue, isTimeValue } from '../../../core/time/helpers.js';
import { useCalendarStore } from '../../../core/time/timeframeStore/useTimeframeStore.js';
import { isOutOfBounds } from '../../../core/time/validation.js';
import { useFormFieldContext } from '../../forms-core/hooks/useFormFieldContext.js';
import { footer as footerStyle } from '../Calendar.sty.js';

/**
 * A button that sets the end time of a range calendar to the current date/time.
 * Disabled when value has already a now or would lead to an error.
 * @internal
 */
export const SetTimeToNowButton = () => {
  const setCalendarValue = useCalendarStore((state) => state.setCalendarValue);
  const calendarValue = useCalendarStore((state) => state.calendarValue);
  const rangeCalendarValue = isTimeValue(calendarValue) ? null : calendarValue;
  const min = useCalendarStore((state) => state.min);
  const max = useCalendarStore((state) => state.max);

  const {
    disabled: contextDisabled,
    ariaDisabled,
    readOnly,
  } = useFormFieldContext();

  const target = useMemo<'from' | 'to'>(() => {
    // When min is a "now" expression the range starts at now, so the button
    // should anchor the start ("from") boundary to the current time.
    if (isNowTimeValue(min)) {
      return 'from';
    }

    const now = TZDateMini.tz(getTimezone());
    if (
      (rangeCalendarValue?.from?.absoluteDate &&
        isAfter(rangeCalendarValue.from.absoluteDate, now)) ||
      (!rangeCalendarValue?.from?.absoluteDate &&
        rangeCalendarValue?.to?.absoluteDate &&
        isAfter(rangeCalendarValue.to.absoluteDate, now))
    ) {
      return 'from';
    }

    return 'to';
  }, [
    min,
    rangeCalendarValue?.from?.absoluteDate,
    rangeCalendarValue?.to?.absoluteDate,
  ]);

  const disabled = useMemo(() => {
    if (isOutOfBounds('now', min, max)) {
      return true;
    }

    const isMinNow = isNowTimeValue(rangeCalendarValue?.from);
    const isMaxNow = isNowTimeValue(rangeCalendarValue?.to);
    return isMinNow || isMaxNow;
  }, [rangeCalendarValue, max, min]);

  // Ref on the footer wrapper so we can restore focus here when the button
  //transitions to disabled (disabled HTML elements cannot be focused).
  const footerRef = useRef<HTMLDivElement>(null);

  const handleClick = useCallback(() => {
    const now = parseTimeAsTimeValue('now');
    if (now) {
      // Move focus to the active calendar date cell
      const dialog = footerRef.current?.closest('[role="dialog"]');
      const activeCell = dialog?.querySelector<HTMLElement>(
        '[role="gridcell"][tabindex="0"]',
      );
      activeCell?.focus();
      setCalendarValue(now);
    }
  }, [setCalendarValue]);

  return (
    <div className={footerStyle} ref={footerRef}>
      <Button
        disabled={contextDisabled || disabled || readOnly}
        aria-disabled={ariaDisabled}
        onClick={handleClick}
        size="condensed"
        variant="emphasized"
      >
        {target === 'from' && !disabled ? (
          <FormattedMessage
            defaultMessage="Set start time to now"
            id="gVTqz8lR6St4GyrT"
            description="Label for the button that sets the start time of a range selection to the current date and time."
          />
        ) : (
          <FormattedMessage
            defaultMessage="Set end time to now"
            id="b16Ain2TkJO1zJU6"
            description="Label for the button that sets the end time of a range selection to the current date and time."
          />
        )}
      </Button>
    </div>
  );
};

SetTimeToNowButton['displayName'] = 'SetTimeToNowButton';
